import { BetSlipUpdatePayload, Context, Handler, OddsFormatIframe } from "../common/types";
import { IframeActions, MsgBusActions, MsgBusEvents } from "../common/events";
import { IframeConfig, WidgetConfig } from "../common/config";
import { betSlipUpdate } from "./betSlipUpdate";
import { logger } from "../services/logger";

type HeightChangePayload = {
  height: number;
};

const getHeightWithExtensions = (element: Element, basicHeight: number) => {
  const computedStyle = getComputedStyle(element);
  const paddingTop = parseInt(computedStyle.paddingTop, 10);
  const paddingBottom = parseInt(computedStyle.paddingBottom, 10);
  return basicHeight + paddingTop + paddingBottom;
};

const handleInitData = (ctx: Context) => {
  const storedData = localStorage.getItem("Sportsbook");
  const parsedData = storedData ? JSON.parse(storedData) : {};
  const oddsFormat = parsedData.oddsFormat || "fraction";

  ctx.iframe.publish(IframeActions.ODDS_FORMAT, {
    format: OddsFormatIframe[oddsFormat],
  });
};

const createIframe = (container: Element, height: number, ctx: Context): void => {
  const iframe = document.createElement("iframe");
  const widgetType = ctx.iframe.getWidgetType();
  iframe.id = `${IframeConfig.ID_PREFIX}-${widgetType}`;
  container.appendChild(iframe);

  const totalHeight = getHeightWithExtensions(iframe, height);
  iframe.style.height = `${totalHeight}px`;

  handleInitData(ctx);
};

const updateExistingIframe = (
  container: Element,
  existingIframe: HTMLIFrameElement,
  existingContainer: Element | null,
  height: number,
  ctx: Context,
): void => {
  // Remove duplicate acca containers
  const accaContainers = container.querySelectorAll(`.${WidgetConfig.CSS_CLASS}`);
  accaContainers.forEach((containerElement) => {
    if (containerElement !== existingContainer) {
      containerElement.remove();
    }
  });

  // Update iframe height
  const totalHeight = getHeightWithExtensions(existingIframe, height);
  existingIframe.style.height = `${totalHeight}px`;

  handleInitData(ctx);
};

const setupOneOfBetSlipUpdate = (ctx: Context): void => {
  // NOTE: this has to be here, otherwise the page refresh will not work.
  // So we have to subscribe and unsubscribe once the data is received
  const token = ctx.msgBus.subscribe(MsgBusEvents.UPDATE_BETSLIP, async (_, payload: BetSlipUpdatePayload) => {
    const unsubscribe = token as string;
    await betSlipUpdate(payload, ctx);
    ctx.msgBus.disposeTopic(unsubscribe);
  });
};

export const heightChange: Handler<HeightChangePayload> = ({ height }, ctx) => {
  const widgetType = ctx.iframe.getWidgetType();
  const widgetId = `${WidgetConfig.ID_PREFIX}-${widgetType}`;
  const selector = ctx.iframe.getSelector();

  //hide widget if there is no data
  if (height < 50) {
    ctx.msgBus.publish(MsgBusActions.STOP, { id: selector });
    return;
  }

  const widgetElement = document.getElementById(widgetId);
  if (!widgetElement) {
    logger.log("Element with provided ID was not found: ", widgetId);
    ctx.iframe.removeSpinner();
    return;
  }
  // Reset height
  widgetElement.style.height = "auto";

  const container = widgetElement.parentElement;
  if (!container) {
    logger.log("Widget element has no parent container");
    return;
  }

  const iframeId = `${IframeConfig.ID_PREFIX}-${widgetType}`;
  const existingIframe = container.querySelector<HTMLIFrameElement>(`#${iframeId}`);
  const existingContainer = container.querySelector(`.${WidgetConfig.CSS_CLASS}`);

  if (!existingIframe) {
    createIframe(container, height, ctx);
  } else {
    updateExistingIframe(container, existingIframe, existingContainer, height, ctx);
  }

  setupOneOfBetSlipUpdate(ctx);
  ctx.iframe.removeSpinner();
};
